import type { Crm } from '@ps/web-martech-cms-types';
import classNames from 'clsx';
import React, { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';

import CrmZone from '@swf/components/CrmZone/CrmZone';
import type { ModalContextValue } from '@swf/components/StarsModal/Context';
import { ModalContext } from '@swf/components/StarsModal/Context';
import type { Variant } from '@swf/components/StarsModal/components/CloseButton';
import CloseButton from '@swf/components/StarsModal/components/CloseButton';
import Header from '@swf/components/StarsModal/components/Header/Header';
import Translation from '@swf/components/Translation/Translation';
import { CRMZone } from '@swf/core/enums';
import { Size } from '@swf/core/models/modal';
import { select } from '@swf/core/store';

import { TXTCLI_My_News } from './StarsCrm.messages.json';
import styles from './StarsCrm.scss';
import { zoneStyles } from './constants';

export interface LocationState {
  className?: string;
  zoneId?: keyof typeof zoneStyles;
  componentType: Crm['component_type'];
  icon?: Variant;
  scrolling?: string;
  size?: Size;
  headerVariant?: string;
}

const getLocationStateClassName = (locationState: LocationState | undefined) =>
  classNames(locationState?.zoneId && zoneStyles[locationState.zoneId], locationState?.className);

const StarsCrmWrapper: React.FC<{ className?: string; children: React.ReactElement }> = ({
  className,
  children
}) => {
  const embeddedModal = useSelector(select.location.isEmbeddedModal);

  return embeddedModal ? (
    <div className={classNames(styles.wrapper, className)}>{children}</div>
  ) : (
    children
  );
};

const StarsCrm: React.FC = () => {
  const { setStyle } = useContext<ModalContextValue>(ModalContext);
  const { zoneID } = useParams<{ zoneID: string }>();
  const { state: locationState } = useLocation<LocationState | undefined>();

  const locationStateClassName = getLocationStateClassName(locationState);

  const {
    headerVariant,
    icon,
    componentType,
    scrolling,
    zoneId: locationStateZoneId
  } = locationState || {};
  const displayMyNewsHeader = locationStateZoneId === CRMZone.MY_NEWS;

  useEffect(() => {
    setStyle({
      size: locationState?.size || Size.MEDIUM,
      className: classNames(styles.modal, locationStateClassName)
    });
  }, [locationState, locationStateClassName, setStyle]);

  return (
    <StarsCrmWrapper className={locationStateClassName}>
      <>
        {displayMyNewsHeader && (
          <Header
            className={classNames(
              styles.header,
              headerVariant && styles[headerVariant as keyof typeof styles]
            )}
          >
            <Translation {...TXTCLI_My_News} />
          </Header>
        )}
        {zoneID !== CRMZone.STARS_REWARDS_OPT_IN && (
          <CloseButton
            variant={icon}
            className={displayMyNewsHeader ? styles.withHeader : styles.withoutHeader}
          />
        )}
        <CrmZone
          zoneId={zoneID}
          componentType={componentType || 'Single'}
          scrolling={scrolling}
          dynamicHeight
        />
      </>
    </StarsCrmWrapper>
  );
};

export default StarsCrm;
